import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AlidadeConfigModel } from '../../../core/services/models/alidade-config.model';
import { ApiResponseModel, ApiResponsePaginationModel } from '../../../shared/models/api-response.model';
import {
  buildTemplatesDashboardParams,
  TemplatesDashboardParams,
} from '../../../shared/models/templates-dashboard-params.model';
import { FilterTargetModel, GroupReportsTemplateModel } from '../store/groupTemplate.model';
import { GroupReportsTemplateDashboardModel } from '../model/group-reports-dashboard.model';
import { CalculateReportModel } from '../model/calculate-report.model';
import {
  GeneratedGroupReportModel,
  GeneratedGroupReportsResponse,
  GeneratedGroupReportsParams,
  GenerationProgressResponse,
} from '../../generated-group-reports/model/generated-group-report.model';
import {
  HtmlGroupReportsParams,
  HtmlGroupReportsResponse,
  PatchHtmlReportBody,
  PatchHtmlReportResponse,
} from '../model/html-group-report.model';
import { GroupActionPlansResponse } from '../model/group-action-plan.model';

/**
 * The template PUT leaves omitted keys unchanged and replaces each list it receives, so every save carries each
 * target and benchmark filter list explicitly, `[]` when nothing is selected, never omitted or `null`.
 */
function withExplicitFilterLists(template: GroupReportsTemplateModel): GroupReportsTemplateModel {
  // A stored filter can come back without a list, or with `null`, whatever the model declares.
  const explicitLists = (group: FilterTargetModel): FilterTargetModel => ({
    ...group,
    distributions: group?.distributions ?? [],
    organizations: group?.organizations ?? [],
    associations: group?.associations ?? [],
    classRanks: group?.classRanks ?? [],
  });
  return {
    ...template,
    filter: {
      ...template.filter,
      target: explicitLists(template.filter?.target),
      benchmark: explicitLists(template.filter?.benchmark),
    },
  };
}

@Injectable({
  providedIn: 'root',
})
export class GroupReportsService {
  private readonly apiUrlReporting: string;

  constructor(
    @Inject('ALIDADE_CONFIG') config: AlidadeConfigModel,
    private http: HttpClient,
  ) {
    this.apiUrlReporting = `${config.apiURL}/reporting`;
  }

  getGroupTemplatesDashboard(
    params?: TemplatesDashboardParams,
  ): Observable<ApiResponsePaginationModel<GroupReportsTemplateDashboardModel[]>> {
    return this.http.get<ApiResponsePaginationModel<GroupReportsTemplateDashboardModel[]>>(
      `${this.apiUrlReporting}/groupTemplatesDashboard`,
      { params: buildTemplatesDashboardParams(params) },
    );
  }

  getGeneratedGroupReports(params?: GeneratedGroupReportsParams): Observable<GeneratedGroupReportsResponse> {
    let httpParams = new HttpParams();
    if (params) {
      if (params.userName) httpParams = httpParams.set('userName', params.userName);
      if (params.templateName) httpParams = httpParams.set('templateName', params.templateName);
      if (params.templateDescription) httpParams = httpParams.set('templateDescription', params.templateDescription);
      if (params.description) httpParams = httpParams.set('description', params.description);
      if (params.sortColumnName) httpParams = httpParams.set('sortColumnName', params.sortColumnName);
      if (params.descSorting != null) httpParams = httpParams.set('descSorting', String(params.descSorting));
      if (params.offsetRows != null) httpParams = httpParams.set('offsetRows', String(params.offsetRows));
      if (params.pageSize != null) httpParams = httpParams.set('pageSize', String(params.pageSize));
    }
    return this.http.get<GeneratedGroupReportsResponse>(
      `${this.apiUrlReporting}/groupTemplatesPDF`,
      { params: httpParams },
    );
  }

  getGeneratedGroupReportById(pdfOid: number): Observable<ApiResponseModel<GeneratedGroupReportModel>> {
    return this.http.get<ApiResponseModel<GeneratedGroupReportModel>>(
      `${this.apiUrlReporting}/groupTemplatesPDF/${pdfOid}`,
    );
  }

  generatePdf(groupTemplateOid: number, html: string, description: string): Observable<{ success: boolean; generationId: string; errorText?: string }> {
    return this.http.post<{ success: boolean; generationId: string; errorText?: string }>(
      `${this.apiUrlReporting}/groupTemplatesPDF/generate/${groupTemplateOid}`,
      { html, description },
    );
  }

  pollGenerationProgress(generationId: string): Observable<GenerationProgressResponse> {
    const headers = new HttpHeaders({ 'X-Skip-Spinner': 'true' });
    return this.http.get<GenerationProgressResponse>(
      `${this.apiUrlReporting}/groupTemplatesPDF/progress/${generationId}`,
      { headers },
    );
  }

  downloadPdf(pdfOid: number): Observable<HttpResponse<Blob>> {
    return this.http.get(
      `${this.apiUrlReporting}/groupTemplatesPDF/${pdfOid}/download`,
      {
        responseType: 'blob',
        observe: 'response',
      }
    );
  }

  deletePdf(pdfOid: number): Observable<ApiResponseModel<void>> {
    return this.http.delete<ApiResponseModel<void>>(`${this.apiUrlReporting}/groupTemplatesPDF/${pdfOid}`);
  }

  loadGroupTemplate(templateOid: number): Observable<ApiResponseModel<GroupReportsTemplateModel[]>> {
    return this.http.get<ApiResponseModel<GroupReportsTemplateModel[]>>(
      `${this.apiUrlReporting}/groupTemplate/${templateOid}`,
    );
  }

  createGroupTemplate(): Observable<ApiResponseModel<GroupReportsTemplateDashboardModel[]>> {
    return this.http.post<ApiResponseModel<GroupReportsTemplateDashboardModel[]>>(
      `${this.apiUrlReporting}/groupTemplate`,
      {},
    );
  }

  fillGroupTemplateData(templateOid: number): Observable<ApiResponseModel<void>> {
    return this.http.put<ApiResponseModel<void>>(
      `${this.apiUrlReporting}/groupTemplate/fillTemplateDomains/${templateOid}`,
      {},
    );
  }

  saveGroupTemplate(templateOid: number, template: GroupReportsTemplateModel): Observable<ApiResponseModel<void>> {
    return this.http.put<ApiResponseModel<void>>(
      `${this.apiUrlReporting}/groupTemplate/${templateOid}`,
      withExplicitFilterLists(template),
    );
  }

  removeGroupTemplate(templateOid: number): Observable<ApiResponseModel<void>> {
    return this.http.delete<ApiResponseModel<void>>(`${this.apiUrlReporting}/groupTemplate/${templateOid}`);
  }

  getCalculatedReport(templateOid: number): Observable<ApiResponseModel<CalculateReportModel[]>> {
    return this.http.get<ApiResponseModel<CalculateReportModel[]>>(
      `${this.apiUrlReporting}/groupTemplate/calculateReport/${templateOid}`,
    );
  }

  getGroupReports(params?: HtmlGroupReportsParams): Observable<HtmlGroupReportsResponse> {
    let httpParams = new HttpParams();
    if (params) {
      if (params.reportName) httpParams = httpParams.set('reportName', params.reportName);
      if (params.templateName) httpParams = httpParams.set('templateName', params.templateName);
      if (params.measureName) httpParams = httpParams.set('measureName', params.measureName);
      if (params.statusName) httpParams = httpParams.set('statusName', params.statusName);
      if (params.sortColumnName) httpParams = httpParams.set('sortColumnName', params.sortColumnName);
      if (params.descSorting != null) httpParams = httpParams.set('descSorting', String(params.descSorting));
      if (params.offsetRows != null) httpParams = httpParams.set('offsetRows', String(params.offsetRows));
      if (params.pageSize != null) httpParams = httpParams.set('pageSize', String(params.pageSize));
    }
    return this.http.get<HtmlGroupReportsResponse>(`${this.apiUrlReporting}/groupResultsReports`, { params: httpParams });
  }

  patchGroupReport(groupReportUUID: string, body: PatchHtmlReportBody): Observable<PatchHtmlReportResponse> {
    return this.http.patch<PatchHtmlReportResponse>(
      `${this.apiUrlReporting}/groupResultsReports/${groupReportUUID}`,
      body,
    );
  }

  deleteGroupReport(groupReportUUID: string): Observable<ApiResponseModel<void>> {
    return this.http.delete<ApiResponseModel<void>>(`${this.apiUrlReporting}/groupResultsReports/${groupReportUUID}`);
  }

  /* Group Action Plans (DOCX deliverable) */

  getGroupActionPlans(params?: HtmlGroupReportsParams): Observable<GroupActionPlansResponse> {
    let httpParams = new HttpParams();
    if (params) {
      if (params.reportName) httpParams = httpParams.set('reportName', params.reportName);
      if (params.templateName) httpParams = httpParams.set('templateName', params.templateName);
      if (params.measureName) httpParams = httpParams.set('measureName', params.measureName);
      if (params.statusName) httpParams = httpParams.set('statusName', params.statusName);
      if (params.sortColumnName) httpParams = httpParams.set('sortColumnName', params.sortColumnName);
      if (params.descSorting != null) httpParams = httpParams.set('descSorting', String(params.descSorting));
      if (params.offsetRows != null) httpParams = httpParams.set('offsetRows', String(params.offsetRows));
      if (params.pageSize != null) httpParams = httpParams.set('pageSize', String(params.pageSize));
    }
    return this.http.get<GroupActionPlansResponse>(`${this.apiUrlReporting}/groupActionPlans`, { params: httpParams });
  }

  patchGroupActionPlan(uuid: string, body: PatchHtmlReportBody): Observable<PatchHtmlReportResponse> {
    return this.http.patch<PatchHtmlReportResponse>(`${this.apiUrlReporting}/groupActionPlans/${uuid}`, body);
  }

  deleteGroupActionPlan(uuid: string): Observable<ApiResponseModel<void>> {
    return this.http.delete<ApiResponseModel<void>>(`${this.apiUrlReporting}/groupActionPlans/${uuid}`);
  }
}
